<template>
  <b-container fluid>
    <page-title icon="fas fa-tachometer-alt" :countdown="countdown" :countdown-max="pollingSeconds">
      <translate>FLEXES Control</translate>
    </page-title>
    <div v-if="!shippingTypesShared.length" class="text-center my-4">
      <b-spinner class="align-middle"></b-spinner>
    </div>
    <b-alert v-else-if="!flexShippingTypeId" variant="info" show v-translate>FLEXES Control is not available for this ecosystem</b-alert>
    <b-card v-else no-body>
      <b-tabs card>
        <b-tab v-for="tab in tabs" :key="tab.kind" :title="tab.title">
          <b-row>
            <b-col cols="3">
              <p>
                <b-form-checkbox :checked="grouping[tab.kind] === 'courier'" @change="setGrouping(tab.kind, 'courier', $event)">
                  <translate>Group by courier</translate>
                </b-form-checkbox>
                <b-form-checkbox :checked="grouping[tab.kind] === 'company'" @change="setGrouping(tab.kind, 'company', $event)">
                  <translate>Group by Company</translate>
                </b-form-checkbox>
              </p>
            </b-col>
            <b-col v-if="tab.kind === 'pending'" class="text-right">
              <b-form-select v-model="hoursToCheck" :options="hoursToCheckOptions" size="sm" class="w-auto" :disabled="bulkChecking"></b-form-select>
              <b-button size="sm" variant="primary" :disabled="bulkChecking" @click.prevent="bulkForcePlatformCheck">
                <b-spinner v-if="bulkChecking" small></b-spinner>
                <span v-if="bulkChecking" key="bulk-progress" v-translate="{ check: checkCount, total: toCheck.length, fixed: checkFixed + checkNoInfo, errors: checkErrors }">Checking %{check}/%{total}. Updated: %{fixed}. Errors: %{errors}</span>
                <span v-else key="bulk-idle" v-translate>Bulk Force Check</span>
              </b-button>
              <span v-translate="{ total: pendingTotal }">FLEX total <small>(last 7 days)</small>: <strong>%{total}</strong>.</span>
              <span v-translate="{ percent: notScannedPercent }">Not scanned: <strong>%{percent}%</strong>.</span>
            </b-col>
          </b-row>
          <b-row v-if="grouping[tab.kind]">
            <b-col v-for="group in groups[tab.kind][grouping[tab.kind]]" :key="group.key" md="3">
              <b-card class="mb-3">
                <b-card-title class="mb-0">
                  <b-link v-if="grouping[tab.kind] === 'courier'" :to="{ name: 'UsersEdit', params: { id: group.id } }" target="_blank">#{{ group.id }}</b-link>
                  {{ group.name }}
                  <b-badge :variant="group.shippings.length < 5 ? 'dark' : 'danger'" href="#" @click.prevent="openDetail(tab.kind, group)">{{ group.shippings.length }}</b-badge>
                </b-card-title>
                <b-card-text v-if="grouping[tab.kind] === 'courier'">
                  <div v-if="group.company" class="mb-2">
                    <span class="badge" :style="companyBadgeStyle(group.company)">{{ group.company.name }}</span>
                  </div>
                  <span v-if="group.phone">
                    <a class="btn btn-sm btn-secondary" style="padding: 0.1rem 1rem;" :href="$options.filters.whatsappize(group.phone)">WhatsApp</a>
                    {{ group.phone }}
                  </span>
                  <b-badge v-else variant="danger" v-translate>NO PHONE!</b-badge>
                </b-card-text>
              </b-card>
            </b-col>
          </b-row>
          <b-table v-else striped hover stacked="md" :items="tab.items" :fields="fields[tab.kind]" show-empty :empty-text="$gettext('There are no rows to show')">
            <template v-slot:cell(id)="row">
              <span class="text-nowrap">
                <b-link :to="{ name: 'ShippingsEdit', params: { id: row.value } }" target="_blank">{{ row.value | uuid }}</b-link>
                <a href="#" @click.prevent="copyToClipboard(row.value)" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a>
              </span>
            </template>
            <template v-slot:cell(courier)="row">
              <user-card v-if="row.item.courier" :user="row.item.courier" :with-titles="false" :profile-picture="false" :vehicle-types-prop="vehicleTypes" />
            </template>
            <template v-slot:cell(company)="row">
              <span v-if="row.item.company">{{ row.item.company.name }}</span>
              <em v-else v-translate>No company</em>
            </template>
            <template v-slot:cell(addressTo)="row">
              {{ row.item.address_to ? row.item.address_to.formatted_address : '-' }}
              <div v-if="tab.kind === 'pickedup'" v-translate="{ status: row.item.status }">Current Status: %{status}</div>
            </template>
            <template v-slot:cell(pickedupAt)="row">
              {{ formatDate(pickupDate(row.item)) }}
            </template>
            <template v-slot:cell(currentStatus)="row">
              {{ row.item.status }}<br>
              {{ formatDate(row.item.status_history.length ? row.item.status_history[0].created_at : null) }}
            </template>
            <template v-slot:cell(delayedBy)="row">
              <div v-if="tab.kind === 'pickedup'">
                {{ pickupDate(row.item) ? elapsedTime(pickupDate(row.item)) : '-' }}
                <b-button size="sm" variant="primary" @click.prevent="forcePlatformCheck(row.item)" v-translate>Check ML Status</b-button>
              </div>
              <div v-else>
                {{ elapsedTime(row.item.delayedSinceDate) }}
                <b-button size="sm" variant="primary" @click.prevent="forcePlatformCheck(row.item)" v-translate>Force Check</b-button>
              </div>
            </template>
            <template v-slot:cell(qr)="row">
              <div v-if="hasQr(row.item)" class="text-center">
                <b-button size="sm" variant="link" style="padding: .25rem" @click.prevent="showQr(row.item.external_label)">
                  <font-awesome-icon icon="qrcode" size="3x" />
                </b-button>
              </div>
            </template>
          </b-table>
        </b-tab>
      </b-tabs>
    </b-card>

    <b-modal ref="modalDetail" size="xl" hide-footer :title="$gettext('FLEXES Details')" @hide="detail = { kind: null, name: null, shippings: [] }">
      <h5>{{ detail.name }}</h5>
      <b-table v-if="detail.kind" striped hover stacked="md" :items="detail.shippings" :fields="fields[detail.kind]" show-empty :empty-text="$gettext('There are no rows to show')">
        <template v-slot:cell(id)="row">
          <span class="text-nowrap">
            <b-link :to="{ name: 'ShippingsEdit', params: { id: row.value } }" target="_blank">{{ row.value | uuid }}</b-link>
            <a href="#" @click.prevent="copyToClipboard(row.value)" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a>
          </span>
        </template>
        <template v-slot:cell(courier)="row">
          <user-card v-if="row.item.courier" :user="row.item.courier" :with-titles="false" :profile-picture="false" :vehicle-types-prop="vehicleTypes" />
        </template>
        <template v-slot:cell(company)="row">
          <span v-if="row.item.company">{{ row.item.company.name }}</span>
          <em v-else v-translate>No company</em>
        </template>
        <template v-slot:cell(addressTo)="row">
          {{ row.item.address_to ? row.item.address_to.formatted_address : '-' }}
          <div v-if="detail.kind === 'pickedup'" v-translate="{ status: row.item.status }">Current Status: %{status}</div>
        </template>
        <template v-slot:cell(pickedupAt)="row">
          {{ formatDate(pickupDate(row.item)) }}
        </template>
        <template v-slot:cell(currentStatus)="row">
          {{ row.item.status }}<br>
          {{ formatDate(row.item.status_history.length ? row.item.status_history[0].created_at : null) }}
        </template>
        <template v-slot:cell(delayedBy)="row">
          <div v-if="detail.kind === 'pickedup'">
            {{ pickupDate(row.item) ? elapsedTime(pickupDate(row.item)) : '-' }}
            <b-button size="sm" variant="primary" @click.prevent="forcePlatformCheck(row.item)" v-translate>Check ML Status</b-button>
          </div>
          <div v-else>
            {{ elapsedTime(row.item.delayedSinceDate) }}
            <b-button size="sm" variant="primary" @click.prevent="forcePlatformCheck(row.item)" v-translate>Force Check</b-button>
          </div>
        </template>
        <template v-slot:cell(qr)="row">
          <div v-if="hasQr(row.item)" class="text-center">
            <b-button size="sm" variant="link" style="padding: .25rem" @click.prevent="showQr(row.item.external_label)">
              <font-awesome-icon icon="qrcode" size="3x" />
            </b-button>
          </div>
        </template>
      </b-table>
    </b-modal>

    <b-modal ref="modalPlatformCheck" hide-footer :title="$gettext('Platform Info Check')" @hide="platformCheck = {}">
      <h4 v-translate="{ status: platformCheck.status }">Status: %{status}</h4>
      <ul>
        <li v-for="(date, key) in platformCheck.statusHistory" :key="key">
          {{ key }}: {{ date ? moment(date).format('L LT') : '-' }}
        </li>
      </ul>
      <b-button v-if="canUpdatePlatformInfo" size="sm" variant="primary" @click.prevent="updateShippingPlatformInfo" v-translate>Update Shipping Data</b-button>
    </b-modal>

    <b-modal ref="modalShowQr" size="md" hide-header hide-footer @hide="qrData = {}">
      <img v-if="qrUrl" :src="qrUrl" alt="">
    </b-modal>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { PlatformInformationService, ShippingService } from '@/services/api'

const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss'
// Same marker as backoffice: fills the platform dates so the shipment leaves the pending list
const PLATFORM_DATE_SENTINEL = '1000-01-01 00:00:00'
const BULK_CHUNK_SIZE = 10
const ONGOING_STATUSES = ['READY', 'WAITING', 'INCIDENCE', 'ATPICKUPPOINT', 'INTRANSIT']
const LIST_OPTIONS = {
  includes: ['company', 'courier', 'status_history'],
  limit: 500
}

export default {
  name: 'FlexesControl',
  title() {
    return this.$gettext('FLEXES Control')
  },
  data() {
    return {
      pollingSeconds: 120,
      pollingTimeout: null,
      countdownInterval: null,
      countdown: 120,
      pickedups: [],
      pendings: [],
      scannedTotal: 0,
      grouping: {
        pickedup: null,
        pending: null
      },
      detail: {
        kind: null,
        name: null,
        shippings: []
      },
      platformCheck: {},
      hoursToCheck: 12,
      toCheck: [],
      checkCount: 0,
      checkFixed: 0,
      checkNoInfo: 0,
      checkErrors: 0,
      bulkChecking: false,
      bulkStopped: false,
      qrData: {}
    }
  },
  computed: {
    ...mapGetters([
      'flexShippingTypeId',
      'shippingTypesShared'
    ]),
    hoursToCheckOptions() {
      return [
        { text: this.$gettext('All 7 days'), value: null },
        { text: this.$gettext('Last 12 hours'), value: 12 },
        { text: this.$gettext('Last 24 hours'), value: 24 },
        { text: this.$gettext('Last 48 hours'), value: 48 }
      ]
    },
    tabs() {
      return [
        { kind: 'pickedup', title: this.$gettext('PICKEDUP FLEX without ML app scan'), items: this.pickedups },
        { kind: 'pending', title: this.$gettext('FLEX with pending ML app scan'), items: this.pendings }
      ]
    },
    fields() {
      const common = [
        { key: 'id', label: this.$gettext('ID') },
        { key: 'courier', label: this.$gettext('Courier') },
        { key: 'company', label: this.$gettext('Company') },
        { key: 'addressTo', label: this.$gettext('Destination') }
      ]
      return {
        pickedup: [
          ...common,
          { key: 'pickedupAt', label: this.$gettext('Picked Up At') },
          { key: 'delayedBy', label: this.$gettext('Delayed By') }
        ],
        pending: [
          ...common,
          { key: 'currentStatus', label: this.$gettext('Status') },
          { key: 'delayedBy', label: this.$gettext('Delayed By') },
          { key: 'qr', label: this.$gettext('QR') }
        ]
      }
    },
    groups() {
      return {
        pickedup: this.groupShippings(this.pickedups),
        pending: this.groupShippings(this.pendings)
      }
    },
    pendingTotal() {
      return this.pendings.length + this.scannedTotal
    },
    notScannedPercent() {
      return (this.pendingTotal ? this.pendings.length * 100 / this.pendingTotal : 0).toFixed(2)
    },
    canUpdatePlatformInfo() {
      const statusHistory = this.platformCheck.statusHistory || {}
      return !!(statusHistory.date_first_visit || statusHistory.date_delivered)
    },
    qrUrl() {
      if (!this.qrData.code && !this.qrData.id) {
        return null
      }
      let data = JSON.stringify(this.qrData)
      if (this.qrData.code) {
        try {
          data = JSON.stringify(JSON.parse(this.qrData.code))
        } catch (e) {
          data = this.qrData.code
        }
      }
      return 'https://api.qrserver.com/v1/create-qr-code/?size=420x420&ecc=L&data=' + encodeURIComponent(data)
    }
  },
  watch: {
    flexShippingTypeId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.startPolling()
        } else {
          this.stopPolling()
        }
      }
    }
  },
  beforeDestroy() {
    this.bulkStopped = true
    this.stopPolling()
  },
  methods: {
    startPolling() {
      this.stopPolling()
      this.reloadData()
      // The bulk check keeps auto-refresh paused and restarts it when it ends
      if (this.bulkChecking) {
        return
      }
      this.countdown = this.pollingSeconds
      this.countdownInterval = setInterval(() => {
        this.countdown = this.countdown === 0 ? 0 : this.countdown - 1
      }, 1000)
      this.pollingTimeout = setTimeout(() => {
        this.startPolling()
      }, this.pollingSeconds * 1000)
    },
    stopPolling() {
      clearTimeout(this.pollingTimeout)
      clearInterval(this.countdownInterval)
      this.pollingTimeout = null
      this.countdownInterval = null
    },
    reloadData() {
      this.fetchPickedups()
      this.fetchPendings()
    },
    buildParams(filters, options = {}) {
      const startDate = this.moment().tz(this.$tz).subtract(7, 'days').hours(this.$appConfig.flexCutOutHour).minutes(0).seconds(1).utc().format(DATE_FORMAT)
      const endDate = this.moment().tz(this.$tz).subtract(2, 'minutes').utc().format(DATE_FORMAT)
      const params = {
        ...options,
        filter_groups: [
          {
            filters: [
              ['shipping_type_id', 'eq', this.flexShippingTypeId],
              ['external_code', 'eq', null, 'true'],
              ['platform_id', 'eq', null, 'true'],
              ['courier_id', 'eq', null, 'true'],
              ['updated_at', 'bt', [startDate, endDate]],
              ...filters
            ]
          }
        ]
      }

      if (!this.$cookies.get('bypassCountry')) {
        params.filter_groups.push(
          {
            filters: [
              ['from_country_iso_code', 'eq', window.country]
            ]
          }
        )
      }

      return params
    },
    fetchPickedups() {
      const params = this.buildParams([
        ['status', 'in', ['INTRANSIT', 'INCIDENCE']],
        ['status_history.status', 'eq', 'INTRANSIT'],
        ['external_label', 'eq', null]
      ], {
        ...LIST_OPTIONS,
        sort: [{
          key: 'updated_at',
          direction: 'desc'
        }]
      })

      return ShippingService.getAll({ params })
        .then(response => {
          this.pickedups = response.body.data
        })
    },
    fetchPendings() {
      const ongoing = [
        ['status', 'in', ONGOING_STATUSES],
        ['status_history.status', 'eq', 'INTRANSIT']
      ]
      const delivered = [
        ['status', 'eq', 'DELIVERED']
      ]
      const requests = [
        this.buildParams([...ongoing, ['platform_first_visit_date', 'eq', null]], LIST_OPTIONS),
        this.buildParams([...delivered, ['platform_delivered_date', 'eq', null]], LIST_OPTIONS),
        this.buildParams([...ongoing, ['platform_first_visit_date', 'eq', null, 'true']], { limit: 1 }),
        this.buildParams([...delivered, ['platform_delivered_date', 'eq', null, 'true']], { limit: 1 })
      ].map(params => ShippingService.getAll({ params }))

      return Promise.all(requests)
        .then(([ongoingPending, deliveredPending, ongoingScanned, deliveredScanned]) => {
          this.pendings = [...ongoingPending.body.data, ...deliveredPending.body.data]
            .filter(shipping => {
              const statusHistory = shipping.status_history || []
              return statusHistory.length && (statusHistory[0].status === 'DELIVERED' || statusHistory.filter(x => x.status === 'INTRANSIT').length > 1)
            })
            .map(shipping => ({ ...shipping, delayedSinceDate: this.delayedSinceDate(shipping.status_history) }))
            .sort((a, b) => a.delayedSinceDate.localeCompare(b.delayedSinceDate))
          this.scannedTotal = (ongoingScanned.body.pagination?.total || 0) + (deliveredScanned.body.pagination?.total || 0)
        })
    },
    // status_history comes newest first
    delayedSinceDate(statusHistory) {
      if (statusHistory[0].status === 'DELIVERED') {
        return statusHistory[0].created_at
      }
      const chronological = [...statusHistory].reverse()
      const index = chronological.findIndex(x => x.status === 'INTRANSIT')
      return (chronological[index + 1] || chronological[index]).created_at
    },
    pickupDate(shipping) {
      const intransit = (shipping.status_history || []).find(x => x.status === 'INTRANSIT')
      return intransit ? intransit.created_at : null
    },
    formatDate(date) {
      return date ? this.moment.utc(date).tz(this.$tz).format('L LT') : '-'
    },
    groupShippings(shippings) {
      const byCourier = {}
      const byCompany = {}
      for (const shipping of shippings) {
        const courier = shipping.courier || {}
        if (!byCourier[shipping.courier_id]) {
          byCourier[shipping.courier_id] = {
            key: shipping.courier_id,
            id: shipping.courier_id,
            name: courier.name,
            phone: courier.phone,
            company: courier.company,
            shippings: []
          }
        }
        byCourier[shipping.courier_id].shippings.push(shipping)

        const companyKey = shipping.company ? shipping.company.id : 'none'
        if (!byCompany[companyKey]) {
          byCompany[companyKey] = {
            key: companyKey,
            name: shipping.company ? shipping.company.name : this.$gettext('No company'),
            shippings: []
          }
        }
        byCompany[companyKey].shippings.push(shipping)
      }
      return {
        courier: Object.values(byCourier),
        company: Object.values(byCompany)
      }
    },
    setGrouping(kind, groupBy, checked) {
      this.grouping[kind] = checked ? groupBy : null
    },
    companyBadgeStyle(company) {
      const colour = this.stringToColour(' ' + company.id + company.id + company.id + company.id)
      return 'background-color: ' + colour + ';' + this.textContrastColor(colour)
    },
    openDetail(kind, group) {
      this.detail = {
        kind,
        name: group.name,
        shippings: group.shippings
      }
      this.$refs.modalDetail.show()
    },
    hasQr(shipping) {
      return !!(shipping.external_label && (shipping.external_label.code || shipping.external_label.id))
    },
    showQr(label) {
      this.qrData = label
      this.$refs.modalShowQr.show()
    },
    // v1 first and, only if it fails, the meli-v2 lookup; rejects with both errors
    fetchPlatformInformation(shipping) {
      const options = { skipErrorAlert: true }
      return PlatformInformationService.get(shipping.id, options)
        .then(response => response, v1Error => PlatformInformationService.getV2(shipping.id, options)
          .then(response => response, v2Error => Promise.reject(Object.assign(new Error('Platform information unavailable'), { v1Error, v2Error }))))
    },
    forcePlatformCheck(shipping) {
      this.fetchPlatformInformation(shipping)
        .then(response => this.openPlatformCheck(shipping, response.body), ({ v1Error, v2Error }) => {
          this.makeToast(this.$gettext('ERROR'), this.platformCheckErrorMessage(v1Error, v2Error), 'danger', false, 5000)
        })
    },
    openPlatformCheck(shipping, body) {
      this.platformCheck = {
        shippingId: shipping.id,
        status: body.status,
        statusHistory: body.status_history || {}
      }
      this.$refs.modalPlatformCheck.show()
    },
    platformCheckErrorMessage(v1Error, v2Error) {
      if (v2Error.status === 502) {
        return this.$gettext('Mercado Libre did not respond, please try again')
      }
      if (v2Error.status === 403 || v1Error.status === 403) {
        return this.$gettext('Your account cannot check the Mercado Libre status yet')
      }
      if (v2Error.status === 404) {
        return this.$gettext('This shipment has no Mercado Libre information')
      }
      return this.$gettext('The Mercado Libre status could not be checked')
    },
    // Stricter than the "no information" message: the bulk writes the sentinel, so a v1 server or
    // network error (which also yields a v2 404 for non-migrated shipments) is not enough
    hasNoPlatformInformation(v1Error, v2Error) {
      return v2Error.status === 404 && v1Error.status >= 400 && v1Error.status < 500 && v1Error.status !== 403
    },
    platformSentinelParams(shipping) {
      const params = {}
      if (!shipping.platform_first_visit_date) {
        params.platform_first_visit_date = PLATFORM_DATE_SENTINEL
      }
      if (!shipping.platform_delivered_date) {
        params.platform_delivered_date = PLATFORM_DATE_SENTINEL
      }
      return params
    },
    bulkPlatformParams(shipping, body) {
      const history = body.status_history || {}
      const params = {}
      if (body.status === 'delivered') {
        if (history.date_first_visit) {
          params.platform_first_visit_date = this.moment(history.date_first_visit).utc().format(DATE_FORMAT)
        }
        if (history.date_delivered) {
          params.platform_delivered_date = this.moment(history.date_delivered).utc().format(DATE_FORMAT)
        }
      } else if (body.status === 'shipped' && !shipping.platform_first_visit_date && history.date_first_visit) {
        params.platform_first_visit_date = this.moment(history.date_first_visit).utc().format(DATE_FORMAT)
      } else if (body.status === 'cancelled') {
        return this.platformSentinelParams(shipping)
      }
      return params
    },
    // Always resolves with 'updated' | 'noInfo' | 'unchanged' | 'error', so one shipment never
    // stops the rest of its chunk
    bulkCheckShipping(shipping) {
      return this.fetchPlatformInformation(shipping)
        .then(response => ({ params: this.bulkPlatformParams(shipping, response.body), outcome: 'updated' }), ({ v1Error, v2Error }) => {
          if (this.hasNoPlatformInformation(v1Error, v2Error)) {
            return { params: this.platformSentinelParams(shipping), outcome: 'noInfo' }
          }
          return { params: null, outcome: 'error' }
        })
        .then(({ params, outcome }) => {
          if (!params) {
            return outcome
          }
          if (!Object.keys(params).length) {
            return 'unchanged'
          }
          return ShippingService.update(shipping.id, params, { skipErrorAlert: true })
            .then(() => outcome)
        })
        .then(outcome => outcome, () => 'error')
    },
    countBulkOutcome(outcome) {
      this.checkCount++
      if (outcome === 'updated') {
        this.checkFixed++
      } else if (outcome === 'noInfo') {
        this.checkNoInfo++
      } else if (outcome === 'error') {
        this.checkErrors++
      }
    },
    async bulkForcePlatformCheck() {
      const since = this.hoursToCheck ? this.moment.utc().subtract(this.hoursToCheck, 'hours') : null
      this.toCheck = this.pendings.filter(shipping => !since || this.moment.utc(shipping.delayedSinceDate) >= since)
      if (!this.toCheck.length) {
        return
      }
      this.checkCount = 0
      this.checkFixed = 0
      this.checkNoInfo = 0
      this.checkErrors = 0
      this.stopPolling()
      this.bulkChecking = true
      try {
        for (let i = 0; i < this.toCheck.length && !this.bulkStopped; i += BULK_CHUNK_SIZE) {
          const chunk = this.toCheck.slice(i, i + BULK_CHUNK_SIZE)
          await Promise.all(chunk.map(shipping => this.bulkCheckShipping(shipping)
            .then(outcome => this.countBulkOutcome(outcome), () => this.countBulkOutcome('error'))))
        }
      } finally {
        this.bulkChecking = false
        if (!this.bulkStopped && this.flexShippingTypeId) {
          const message = this.$gettextInterpolate(this.$gettext('Bulk check finished: %{checked} checked, %{fixed} updated, %{noInfo} without ML information, %{errors} errors'), {
            checked: this.checkCount,
            fixed: this.checkFixed,
            noInfo: this.checkNoInfo,
            errors: this.checkErrors
          })
          this.makeToast(this.$gettext('Bulk Force Check'), message, this.checkErrors ? 'warning' : 'success', false, 8000)
          this.startPolling()
        }
      }
    },
    updateShippingPlatformInfo() {
      const statusHistory = this.platformCheck.statusHistory
      const params = {}
      if (statusHistory.date_first_visit) {
        params.platform_first_visit_date = this.moment(statusHistory.date_first_visit).utc().format(DATE_FORMAT)
      }
      if (statusHistory.date_delivered) {
        params.platform_delivered_date = this.moment(statusHistory.date_delivered).utc().format(DATE_FORMAT)
      }
      ShippingService.update(this.platformCheck.shippingId, params)
        .then(() => {
          this.$refs.modalPlatformCheck.hide()
          this.startPolling()
        })
    }
  }
}
</script>
